import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import TermsDialog from "@/components/TermsDialog";
import WhatsAppLoginDialog from "@/components/WhatsAppLoginDialog";
import {
  Truck, PackageSearch, MessageSquare, ShieldCheck,
  Thermometer, MapPin, ArrowRight, Zap, Users, MessageCircle,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1779517225512-ec62df745e7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NxA1Mjh8MHwxfHNlYXJjaHw0fHxyZWZyaWdlcmF0ZWQlMjB0cnVjayUyMGxvZ2lzdGljcyUyMGNvbGQlMjBjaGFpbiUyMHRyYW5zcG9ydGF0aW9uJTIwdHJ1Y2slMjBkcml2ZXIlMjBjYXJnb3xlbnwwfHx8fDE3ODgyNTUwMTB8MA&ixlib=rb-4.1.0&q=85";

const login = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Landing() {
  const { auth, loading } = useAuth();
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [termsReadOnly, setTermsReadOnly] = useState(false);
  const [showWa, setShowWa] = useState(false);

  const startSignIn = () => { setTermsReadOnly(false); setShowTerms(true); };
  const openTerms = () => { setTermsReadOnly(true); setShowTerms(true); };

  useEffect(() => {
    if (!loading && auth) navigate("/app", { replace: true });
  }, [auth, loading, navigate]);

  const features = [
    { icon: PackageSearch, title: "Post Loads", desc: "Origin, destination, cargo, weight, temperature & truck type — live for the whole network." },
    { icon: Truck, title: "Post Trucks", desc: "Broadcast reefer availability with capacity, location & temperature range in seconds." },
    { icon: MessageSquare, title: "Connect Instantly", desc: "One-tap WhatsApp chat plus built-in messaging between transporters." },
    { icon: Thermometer, title: "Temperature Matched", desc: "Every posting is tagged with its reefer range so cargo never breaks the cold chain." },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="frost-aurora absolute inset-0 z-0" />
      <div className="ice-grid absolute inset-0 z-0 h-[70vh]" />
      {/* Nav */}
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-5 border-b border-border/60 glass">
        <div className="flex items-center gap-2.5">
          <img src="/logo-icon.png" alt="Reefer Sathi logo" data-testid="landing-logo" className="w-9 h-9 object-contain drop-shadow-[0_0_16px_hsl(var(--primary)/0.35)]" />
          <span className="font-heading font-extrabold text-xl tracking-tight">Reefer Sathi</span>
        </div>
        <div className="flex items-center gap-2.5">
          <Button data-testid="nav-whatsapp-btn" onClick={() => setShowWa(true)} variant="outline"
            className="rounded-full font-semibold border-[#25D366]/50 text-[#1ebe5b] hover:bg-[#25D366]/10 hover:text-[#1ebe5b] transition-colors">
            <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
          </Button>
          <Button data-testid="nav-login-btn" onClick={startSignIn} className="rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors active:scale-95">
            Sign in with Google
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 grid lg:grid-cols-2 gap-12 items-center px-6 lg:px-12 pt-16 pb-24 max-w-7xl mx-auto">
        <div className="animate-fadeup">
          <img src="/logo-full.png" alt="ReeferSathi.com" data-testid="hero-logo" className="h-24 sm:h-28 w-auto object-contain mb-8 drop-shadow-[0_6px_24px_hsl(var(--primary)/0.25)]" />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 mb-6">
            <Zap className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">Cold Chain Load Exchange</span>
          </div>
          <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Stop scrolling <span className="text-frost">WhatsApp groups.</span> Move reefer freight faster.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            Reefer Sathi turns scattered load & truck messages into one live, searchable board built for cold chain transporters. Post it once, match it fast, chat instantly.
          </p>
          <div className="mt-8 flex flex-col gap-3 max-w-sm">
            <Button data-testid="hero-cta-btn" onClick={startSignIn} size="lg" className="rounded-full font-semibold h-12 px-7 cta-accent group w-full sm:w-auto">
              Continue with Google — 60 day trial
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button data-testid="hero-whatsapp-btn" onClick={() => setShowWa(true)} size="lg" variant="outline"
              className="rounded-full font-semibold h-12 px-7 w-full sm:w-auto border-[#25D366]/50 text-[#1ebe5b] hover:bg-[#25D366]/10 hover:text-[#1ebe5b]">
              <MessageCircle className="w-4 h-4 mr-1.5" /> Continue with WhatsApp
            </Button>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
              <ShieldCheck className="w-4 h-4 text-primary" /> No WhatsApp chaos. Cancel anytime.
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground/80">
            By continuing you agree to our{" "}
            <button data-testid="hero-terms-link" onClick={openTerms} className="text-primary underline underline-offset-2 hover:opacity-80">Terms & Conditions</button>.
          </p>
          <div className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> Verified transporters</div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> Pan-India routes</div>
          </div>
        </div>
        <div className="relative animate-fadeup" style={{ animationDelay: "0.15s" }}>
          <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-full" />
          <img src={HERO} alt="Refrigerated truck" className="relative rounded-2xl border border-border w-full h-[440px] object-cover shadow-2xl" />
          <div className="absolute -bottom-5 -left-5 bg-card border border-border rounded-xl px-5 py-4 shadow-xl backdrop-blur">
            <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-widest mb-1">
              <Thermometer className="w-3.5 h-3.5" /> Live reefer
            </div>
            <div className="font-mono-num text-2xl font-bold">-18°C <span className="text-muted-foreground text-sm">→ +4°C</span></div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 px-6 lg:px-12 pb-24 max-w-7xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <div key={f.title} className="rounded-xl border border-border bg-card/60 p-6 hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-300 animate-fadeup" style={{ animationDelay: `${0.05 * i}s` }}>
            <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 shadow-[0_0_18px_hsl(var(--primary)/0.25)]">
                <f.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-1.5">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 border-t border-border/60 px-6 lg:px-12 py-8 text-sm text-muted-foreground flex flex-wrap justify-between gap-4">
        <span>© {new Date().getFullYear()} Reefer Sathi — Cold Chain Transport Network</span>
        <div className="flex items-center gap-4">
          <a href="mailto:info@reefersathi.com" data-testid="footer-contact-link" className="hover:text-primary transition-colors">info@reefersathi.com</a>
          <button data-testid="footer-terms-link" onClick={openTerms} className="hover:text-primary transition-colors">Terms & Conditions</button>
          <span className="font-mono text-xs">Built for reefer transporters</span>
        </div>
      </footer>

      <TermsDialog open={showTerms} onOpenChange={setShowTerms} onAccept={login} readOnly={termsReadOnly} />
      <WhatsAppLoginDialog open={showWa} onOpenChange={setShowWa} onSuccess={() => navigate("/app", { replace: true })} />
    </div>
  );
}
